import { fetchCurrentUser } from '../../ducks/user.duck';
import {
  ACCOUNT_ROLE_BOTH,
  ACCOUNT_ROLE_LISTEN,
  ACCOUNT_ROLE_TALK,
  cancelListenerOnboardingData,
  chosenAccountRoleData,
  completeListenerOnboardingData,
  getAccountRole,
  userTypeForAccountRole,
} from '../../util/accountRole';
import { listenerOnboardingStartedData } from '../../util/listenerOnboarding';
import { listingIdFromOwnListing } from '../../util/ownListeningProfile';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { storableError } from '../../util/errors';

/**
 * Persist Talk / Listen / Both on public and private data, then refresh currentUser.
 *
 * @param {Object} params
 * @param {'talk'|'listen'|'both'} params.accountRole
 * @param {Array} [params.userTypes]
 * @returns {Function}
 */
export const saveAccountRole = ({ accountRole, userTypes = [] }) => (dispatch, getState, sdk) => {
  if (!sdk?.currentUser?.updateProfile) {
    return Promise.resolve(accountRole);
  }
  const userType = userTypeForAccountRole(accountRole, userTypes);
  return sdk.currentUser
    .updateProfile(chosenAccountRoleData(accountRole, userType), { expand: true })
    .then(() => dispatch(fetchCurrentUser({ enforce: true })))
    .then(() => accountRole)
    .catch(e => {
      throw storableError(e);
    });
};

/**
 * Mark that a talker started Become a listener without flipping the role.
 *
 * @returns {Function}
 */
export const startListenerOnboarding = () => (dispatch, getState, sdk) => {
  if (!sdk?.currentUser?.updateProfile) {
    return Promise.resolve(true);
  }
  return sdk.currentUser
    .updateProfile(listenerOnboardingStartedData(true), { expand: true })
    .then(() => dispatch(fetchCurrentUser({ enforce: true })))
    .then(() => true)
    .catch(e => {
      throw storableError(e);
    });
};

/**
 * Write Listen / Both and clear the started flag after a published profile.
 *
 * @param {Object} [params]
 * @param {Array} [params.userTypes]
 * @returns {Function}
 */
export const completeListenerOnboarding = ({ userTypes = [] } = {}) => (
  dispatch,
  getState,
  sdk
) => {
  if (!sdk?.currentUser?.updateProfile) {
    return Promise.resolve(ACCOUNT_ROLE_BOTH);
  }
  const currentUser = getState()?.user?.currentUser;
  const previous = getAccountRole(currentUser);
  const accountRole = previous === ACCOUNT_ROLE_LISTEN ? ACCOUNT_ROLE_LISTEN : ACCOUNT_ROLE_BOTH;
  const userType = userTypeForAccountRole(accountRole, userTypes);
  return sdk.currentUser
    .updateProfile(completeListenerOnboardingData(accountRole, userType), { expand: true })
    .then(() => dispatch(fetchCurrentUser({ enforce: true })))
    .then(() => accountRole)
    .catch(e => {
      throw storableError(e);
    });
};

const listingStateOf = listing => listing?.attributes?.state || listing?.state || null;

/**
 * Discard leftover drafts, write Talk-only, and clear the started flag.
 *
 * @param {Object} [params]
 * @param {Array} [params.userTypes]
 * @param {Array} [params.ownListings]
 * @returns {Function}
 */
export const cancelListenerOnboarding = ({ userTypes = [], ownListings = [] } = {}) => (
  dispatch,
  getState,
  sdk
) => {
  const drafts = (Array.isArray(ownListings) ? ownListings : []).filter(
    listing => listingStateOf(listing) === LISTING_STATE_DRAFT
  );
  const discardDrafts = drafts.reduce((chain, listing) => {
    const id = listingIdFromOwnListing(listing);
    if (!id || typeof sdk?.ownListings?.discardDraft !== 'function') {
      return chain;
    }
    return chain.then(() => sdk.ownListings.discardDraft({ id }).catch(() => null));
  }, Promise.resolve());

  const persistTalk = () => {
    if (!sdk?.currentUser?.updateProfile) {
      return Promise.resolve(ACCOUNT_ROLE_TALK);
    }
    const userType = userTypeForAccountRole(ACCOUNT_ROLE_TALK, userTypes);
    return sdk.currentUser
      .updateProfile(cancelListenerOnboardingData(userType), { expand: true })
      .then(() => dispatch(fetchCurrentUser({ enforce: true, updateHasListings: true })))
      .then(() => ACCOUNT_ROLE_TALK);
  };

  return discardDrafts.then(persistTalk).catch(e => {
    throw storableError(e);
  });
};
